import React, { useState, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { addAttachmentToBond } from "../../services/BondsServices";
import {
  ENVIRONMENT,
  SCAN_SIGNED_URL_API,
  SCAN_STATUS_URL_API,
} from "../../creds";
import { cleanFileName } from "../../utils/nameTextCleaner";

const UploadModal = ({ bond_id, handleClose }) => {
  const user = useSelector((state) => state.user);

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);

  // Step A states
  const [isScanning, setIsScanning] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Step B states
  const [isUploading, setIsUploading] = useState(false);

  /******************************************************
   * 1. safeFetch: central error handling
   ******************************************************/
  const safeFetch = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errMessage = await response.text();
      throw new Error(
        errMessage || `\nRequest failed with status ${response.status}`
      );
    }
    return response;
  };

  /******************************************************
   * 2. checkAttachmentIfExists
   ******************************************************/
  const checkAttachmentIfExists = useCallback(
    async (file) => {
      const requestBody = {
        IsUploadFlag: false,
        Attachments: [
          {
            bond_issue_number: bond_id,
            email_address: user?.userinfo?.email,
            Attachment_Name: file.name,
            LastUpdDttm: "",
            Attachment_Status: "",
            Is_Deleted_Flag: "N",
          },
        ],
      };

      const response = await addAttachmentToBond(requestBody);
      const data = response?.[0];
      const fileCheck = data?.Attachments?.[0]?.checkAttachmentExists;
      if (fileCheck === undefined) {
        throw new Error("\nError checking if file is duplicate");
      }
      return fileCheck; // "true" or "false"
    },
    [bond_id, user]
  );

  /******************************************************
   * 3. getSignedUrl
   ******************************************************/
  const getSignedUrl = useCallback(
    async (file) => {
      const body = JSON.stringify({
        fileName: `bondregistration/${bond_id}/${file.name}`,
      });
      const response = await safeFetch(SCAN_SIGNED_URL_API, {
        method: "POST",
        body,
      });
      const data = await response.json();
      return data.data.replace(/['"]+/g, "");
    },
    [bond_id]
  );

  /******************************************************
   * 4. uploadFile (S3 only) - No scanning status polling
   ******************************************************/
  const uploadFileToS3 = useCallback(
    async (signedUrl, file) => {
      await safeFetch(signedUrl, {
        method: "PUT",
        headers: {
          "x-amz-acl": "bucket-owner-full-control",
          "x-amz-server-side-encryption": "AES256",
          "x-amz-meta-profile": ENVIRONMENT,
        },
        body: file,
      });
      // No scanning poll here
    },
    []
  );

  /******************************************************
   * 5. getFileUploadStatus (polling for scanning)
   ******************************************************/
  const getFileUploadStatus = useCallback(
    async (file, maxRetries = 10, intervalMs = 5000) => {
      let attempt = 0;

      const poll = async () => {
        attempt++;
        const response = await safeFetch(SCAN_STATUS_URL_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileKey: `bondregistration/${bond_id}/${file.name}`,
          }),
        });
        const data = await response.json();

        if (!data?.data || data.data === "null") {
          if (attempt >= maxRetries) {
            throw new Error(
              `\nTimeout while scanning file ${file.name}. Please try again.`
            );
          }
          // Wait and retry
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          return poll();
        }

        // We have valid data
        const resData = JSON.parse(data.data);
        if (!resData?.key) {
          throw new Error(
            `\nSystem Error occurred while scanning file ${file.name}.`
          );
        }
        // Return the final object
        return {
          bond_issue_number: bond_id,
          email_address: user?.userinfo?.email,
          Attachment_Name: resData.key.split(`bondregistration/${bond_id}/`)[1],
          LastUpdDttm: resData.lastUpdated,
          Attachment_Status: resData.status,
          Is_Deleted_Flag: "N",
          checkAttachmentExists: "false",
        };
      };

      return poll();
    },
    [bond_id, user]
  );

  /******************************************************
   * 6. Step A: SCAN (actually just S3 upload)
   ******************************************************/
  const handleScan = useCallback(async () => {
    setIsScanning(true);
    setErrors([]);
    setUploadedFiles([]); // reset from previous tries

    try {
      // We'll gather the successfully uploaded files in an array
      const uploaded = [];

      await Promise.all(
        files.map(async (file) => {
          try {
            // 1. Check if file name exists
            const isFileNameExist = await checkAttachmentIfExists(file);
            if (isFileNameExist === "true") {
              throw new Error(
                `\nFile ${file.name} already exists.\n` +
                  "To replace, please delete the existing one first."
              );
            }

            // 2. Get Signed URL
            const signedUrl = await getSignedUrl(file);

            // 3. Upload to S3 (no polling)
            await uploadFileToS3(signedUrl, file);

            // If successful, push to uploaded array
            uploaded.push(file);
          } catch (err) {
            setErrors((prev) => [...prev, err.message]);
          }
        })
      );

      // If we have any successful uploads, store them
      if (uploaded.length > 0) {
        setUploadedFiles(uploaded);
      }
    } catch (err) {
      setErrors((prev) => [...prev, err.message]);
    } finally {
      setIsScanning(false);
    }
  }, [files, checkAttachmentIfExists, getSignedUrl, uploadFileToS3]);

  /******************************************************
   * 7. Step B: UPLOAD scanned files (poll + attach)
   ******************************************************/
  const handleUploadScannedFiles = useCallback(async () => {
    if (uploadedFiles.length === 0) {
      return; // Nothing to process
    }

    setIsUploading(true);
    setErrors([]);

    const finalRequests = [];

    try {
      // For each previously "uploaded" file, now poll for scanning
      const results = await Promise.all(
        uploadedFiles.map(async (file) => {
          try {
            return await getFileUploadStatus(file);
          } catch (err) {
            setErrors((prev) => [...prev, err.message]);
            return null; // keep going for the rest
          }
        })
      );

      // Filter out any null (failed scanning) requests
      const successfulScanResults = results.filter((r) => r !== null);
      if (successfulScanResults.length > 0) {
        finalRequests.push(...successfulScanResults);
      }

      if (finalRequests.length > 0) {
        // Call addAttachmentToBond with all scanned requests
        const uploadRequest = {
          IsUploadFlag: true,
          Attachments: finalRequests,
        };
        await addAttachmentToBond(uploadRequest);

        // If you'd like to close the modal when done:
        handleClose();
      }
    } catch (err) {
      setErrors((prev) => [...prev, err.message]);
    } finally {
      setIsUploading(false);
    }
  }, [uploadedFiles, getFileUploadStatus, handleClose]);

  /******************************************************
   * handleFileChange
   ******************************************************/
  const handleFileChange = (e) => {
    setErrors([]);
     const selectedFiles = Array.from(e.target.files)
      .filter((file) => file.type === "application/pdf")
      .map((file) => {
        // Clean the filename before using it
        const cleanedName = cleanFileName(file.name);
        const renamedFile = new File([file], cleanedName, { type: file.type });
        // Create a new File object with the cleaned name
        return renamedFile;
      });

    setFiles(selectedFiles);
  };

  return (
    <>
   <div style={{ marginTop: "10px" }}>

{/* File input */}
<div style={{ marginBottom: "10px" }}>
  <input
    type="file"
    accept=".pdf"
    multiple
    onChange={handleFileChange}
    disabled={isScanning || isUploading}
  />
</div>

{/* Step A: SCAN Files (S3 upload) */}
<div style={{ marginBottom: "10px" }}>
  {!isScanning ? (
    <>
    {/* Show selected cleaned filenames */}
    {files.length > 0 && (
      <div style={{ marginTop: "10px" }}>
        <strong>Selected Files (auto-renamed):</strong>
        <ul>
          {files.map((file, idx) => (
            <li key={idx}>{file.name}</li>
          ))}
        </ul>
      </div>
    )}
    <Button
      variant="primary"
      onClick={handleScan}
      disabled={files.length === 0 || isUploading}
    >
      Scan Files
    </Button>
    <div style={{ marginTop: "5px", color: "red" }}>
      Please upload .pdf documents only
    </div>
    <div style={{ marginTop: "5px"}}>
      Do not upload documents that are not part of the final official transcript, including the Texas Bond Review Board State and Local Government Issuance Data Collection Workbook authorized to be collected by the OAG by Texas Government Code, Section 1202.008.
    </div>
    </>
  ) : (
    <Button variant="primary" disabled>
      <Spinner
        as="span"
        animation="grow"
        size="sm"
        role="status"
        aria-hidden="true"
      />
      Scanning...
    </Button>
  )}

  {/* PDF Only Message - on its own line */}

</div>

{/* Step B: UPLOAD (poll + finalize) on its own line */}
<div style={{ marginBottom: "10px" }}>
  {uploadedFiles.length > 0 && !isScanning && (
    !isUploading ? (
      <Button variant="success" onClick={handleUploadScannedFiles}>
        Upload Scanned Files
      </Button>
    ) : (
      <Button variant="success" disabled>
        <Spinner
          as="span"
          animation="grow"
          size="sm"
          role="status"
          aria-hidden="true"
        />
        Uploading...
      </Button>
    )
  )}
</div>

{/* Error messages */}
{errors.length > 0 && (
  <div style={{ color: "red", marginBottom: "10px" }}>
    {errors.map((error, idx) => (
      <div key={idx}>{error}</div>
    ))}
  </div>
)}

{/* Show the list of "uploaded" files (optional) */}
{uploadedFiles.length > 0 && (
  <div>
    <strong>Files Scanned (pending final upload):</strong>
    <ul>
      {uploadedFiles.map((file) => (
        <li key={file.name}>{file.name}</li>
      ))}
    </ul>
  </div>
)}
</div>

    </>
  );
};

export default UploadModal;
